import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { FileText } from "lucide-react";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, typographyScale, webTransitions, typographyStyles } from "../../../../styles/design-system";

export interface AssignmentSuggestion {
  id: string;
  title?: string | null;
  due_date?: string | null;
}

interface AssignmentSuggestionCardProps {
  assignment: AssignmentSuggestion;
  onPress: (assignment: AssignmentSuggestion) => void;
}

export const AssignmentSuggestionCard: React.FC<AssignmentSuggestionCardProps> = ({ assignment, onPress }) => {
  return (
    <Pressable
      style={({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
        styles.card,
        hovered && styles.cardHovered,
        pressed && styles.cardHovered,
      ]}
      onPress={() => onPress(assignment)}
      role="button"
      accessibilityLabel={assignment.title || "Untitled Assignment"}>
      <View style={styles.iconContainer}>
        <FileText size={20} color={colors.brand[600]} />
      </View>
      <View style={styles.content}>
        <CustomText
          text={assignment.title || "Untitled Assignment"}
          useTranslationText={false}
          style={styles.title}
          numberOfLines={1}
        />
        {assignment.due_date && (
          <CustomText
            text={`Due ${new Date(assignment.due_date).toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}`}
            useTranslationText={false}
            style={styles.dueDate}
          />
        )}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: radii.lg,
    backgroundColor: colors.white,
    cursor: "pointer",
    ...webTransitions.fast,
  },
  cardHovered: {
    borderColor: colors.brand[300],
    backgroundColor: colors.brand[50],
  },
  iconContainer: {
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: typographyStyles.fontRegular.fontWeight,
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[900],
  },
  dueDate: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[600],
  },
});
